import { useState, useEffect, useCallback, useRef } from 'react';
import {
  makeStyles,
  tokens,
  Text,
  Button,
  Spinner,
  mergeClasses,
  Input,
} from '@fluentui/react-components';
import {
  ChatMultipleRegular,
  DeleteRegular,
  AddRegular,
  EditRegular,
  CheckmarkRegular,
  DismissRegular,
} from '@fluentui/react-icons';
import * as chatHistoryClient from '@/services/chatHistoryClient';
import type { SavedChatSummary, ChatHistoryFetchOptions } from '@/services/chatHistoryClient';
import { isLocalChatHistoryRuntime } from '@/services/chatHistoryClient';
import { useAgentChatAguiStore } from '@/stores/agentChatAguiStore';
import { useAppStore } from '@/stores/appStore';
import { useAuth } from '@/auth/useAuth';

const useStyles = makeStyles({
  panel: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  // Header with New Chat button and count
  listHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: tokens.spacingVerticalM,
    paddingInline: tokens.spacingHorizontalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    marginBottom: tokens.spacingVerticalS,
  },
  newButton: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    border: 'none',
    backgroundColor: 'transparent',
    color: tokens.colorBrandForeground1,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  chatCount: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  chatCountNumber: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  content: {
    flex: 1,
    overflowY: 'auto',
  },
  chatList: {
    display: 'flex',
    flexDirection: 'column',
  },
  // Chat card - styled like run cards
  chatCard: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalM,
    paddingInline: tokens.spacingHorizontalM,
    cursor: 'pointer',
    border: 'none',
    width: '100%',
    textAlign: 'left',
    backgroundColor: 'transparent',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    borderLeft: '3px solid transparent',
    transition: 'all 0.15s ease',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      transform: 'translateX(2px)',
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '-2px',
    },
  },
  chatCardSelected: {
    backgroundColor: tokens.colorBrandBackground2,
    borderLeftColor: tokens.colorBrandStroke1,
    transform: 'none',
    ':hover': {
      backgroundColor: tokens.colorBrandBackground2,
      transform: 'none',
    },
  },
  chatIcon: {
    width: '32px',
    height: '32px',
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: tokens.colorNeutralForeground3,
  },
  chatInfo: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
  },
  chatName: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  chatMeta: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  chatActions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    opacity: 0,
    transition: 'opacity 0.15s ease',
  },
  chatActionsVisible: {
    opacity: 1,
  },
  // Empty state
  emptyState: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalXXL,
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: '48px',
    color: tokens.colorNeutralForeground4,
    marginBottom: tokens.spacingVerticalS,
  },
  emptyTitle: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  emptySubtitle: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground3,
    maxWidth: '240px',
    lineHeight: tokens.lineHeightBase300,
  },
  loadingContainer: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXL,
  },
  footer: {
    padding: tokens.spacingVerticalS,
    paddingInline: tokens.spacingHorizontalM,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    textAlign: 'center',
  },
  chatItemWrapper: {
    position: 'relative',
  },
  customTooltip: {
    position: 'fixed',
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow16,
    zIndex: 10000,
    maxWidth: '300px',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
    pointerEvents: 'none',
  },
  renameInput: {
    flex: 1,
    minWidth: 0,
  },
  renameActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalXXS,
  },
  renameContainer: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    alignItems: 'center',
  },
});

interface ChatHistoryPanelProps {
  /** The current workflow name to filter chats */
  workflowName: string;
  /** Currently active chat ID */
  activeChatId?: string | undefined;
  /** Callback when a chat is selected to open */
  onOpenChat: (chatId: string) => void;
  /** Callback to start a new chat */
  onNewChat: () => void;
  /** Whether the panel is visible */
  isVisible: boolean;
}

/**
 * Format a date for display in the chat list
 */
function formatChatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return date.toLocaleDateString(undefined, { weekday: 'short' });
  } else {
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
}

export const ChatHistoryPanel = ({
  workflowName,
  activeChatId,
  onOpenChat,
  onNewChat,
  isVisible,
}: ChatHistoryPanelProps) => {
  const styles = useStyles();
  const [chats, setChats] = useState<SavedChatSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [maxChats, setMaxChats] = useState(20);
  const [hoveredChatId, setHoveredChatId] = useState<string | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null);
  const [renamingChatId, setRenamingChatId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  // Track if we've already loaded chats for this workflow to prevent infinite retries
  const hasLoadedRef = useRef(false);
  const lastWorkflowRef = useRef<string | null>(null);
  // Subscribe to chat history version to refresh when a new chat is saved
  const chatHistoryVersion = useAgentChatAguiStore((s) => s.chatHistoryVersion);

  // Get app context and auth for production mode API calls
  const { selectedProject, selectedApp } = useAppStore();
  const { getOttoToken } = useAuth();

  // Build fetch options for chat history API
  const getFetchOptions = useCallback(async (): Promise<ChatHistoryFetchOptions | undefined> => {
    const token = await getOttoToken();
    if (!token) {
      console.warn('Failed to get Otto token for chat history API');
      return undefined;
    }

    if (isLocalChatHistoryRuntime()) {
      // In local mode, only accessToken is needed (URL is built from localhost)
      return {
        projectName: '', // Not needed in local mode but keeping for type compatibility
        appName: '', // Not needed in local mode but keeping for type compatibility
        accessToken: token,
      };
    }

    return {
      projectName: selectedProject ?? '',
      appName: selectedApp?.name ?? '',
      accessToken: token,
    };
  }, [getOttoToken, selectedProject, selectedApp?.name]);

  // Load chats function - use ref to avoid dependency issues
  const loadChatsRef = useRef<(force?: boolean) => Promise<void>>();
  
  loadChatsRef.current = async (force = false) => {
    if (!workflowName) return;

    // Prevent infinite retries - only load once per workflow unless forced
    if (!force && hasLoadedRef.current && lastWorkflowRef.current === workflowName) {
      return;
    }

    // Only show loading spinner on initial load, not on refresh (prevents flickering)
    const isInitialLoad = !hasLoadedRef.current;
    if (isInitialLoad) {
      setIsLoading(true);
    }
    setError(null);

    try {
      const opts = await getFetchOptions();
      const response = await chatHistoryClient.listChats(workflowName, opts);
      
      // Only update state if the data has actually changed (prevents flickering)
      setChats(prevChats => {
        const hasChanged = prevChats.length !== response.chats.length ||
          prevChats.some((chat, i) => {
            const newChat = response.chats[i];
            return !newChat || chat.id !== newChat.id || chat.name !== newChat.name || chat.updatedAt !== newChat.updatedAt;
          });
        return hasChanged ? response.chats : prevChats;
      });
      setMaxChats(response.maxChats);
      hasLoadedRef.current = true;
      lastWorkflowRef.current = workflowName;
    } catch (err) {
      console.error('Failed to load chat history:', err);
      setError('Failed to load chat history');
      // Mark as loaded to prevent infinite retries on error
      hasLoadedRef.current = true;
      lastWorkflowRef.current = workflowName;
    } finally {
      if (isInitialLoad) {
        setIsLoading(false);
      }
    }
  };

  // Load chats when visible - only depends on stable values
  useEffect(() => {
    if (isVisible && workflowName) {
      loadChatsRef.current?.(false);
    }
  }, [isVisible, workflowName]);

  // Reload when chatHistoryVersion changes (new chat saved)
  useEffect(() => {
    if (isVisible && workflowName && hasLoadedRef.current) {
      loadChatsRef.current?.(true);
    }
  }, [chatHistoryVersion, isVisible, workflowName]);

  // Reset hasLoaded when workflow changes
  useEffect(() => {
    if (workflowName !== lastWorkflowRef.current) {
      hasLoadedRef.current = false;
    }
  }, [workflowName]);

  // Handler to open a chat with proper auth
  const handleOpenChat = useCallback(async (chatId: string) => {
    try {
      const opts = await getFetchOptions();
      // Load the chat into the store with auth
      await useAgentChatAguiStore.getState().loadChat(workflowName, chatId, opts);
      // Also call the provided callback for any additional handling (e.g., closing panel)
      onOpenChat(chatId);
    } catch (err) {
      console.error('Failed to open chat:', err);
      setError('Failed to open chat');
    }
  }, [workflowName, getFetchOptions, onOpenChat]);

  const handleDeleteChat = async (chatId: string, event: React.MouseEvent) => {
    event.stopPropagation();

    if (!confirm('Are you sure you want to delete this chat?')) {
      return;
    }

    try {
      const opts = await getFetchOptions();
      await chatHistoryClient.deleteChat(workflowName, chatId, opts);
      setChats((prev) => prev.filter((c) => c.id !== chatId));
    } catch (err) {
      console.error('Failed to delete chat:', err);
      setError('Failed to delete chat');
    }
  };

  const handleStartRename = (chatId: string, currentName: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setRenamingChatId(chatId);
    setRenameValue(currentName);
  };

  const handleCancelRename = (event?: React.MouseEvent) => {
    event?.stopPropagation();
    setRenamingChatId(null);
    setRenameValue('');
  };

  const handleConfirmRename = async (chatId: string, event?: React.MouseEvent | React.KeyboardEvent) => {
    event?.stopPropagation();
    
    const trimmedName = renameValue.trim();
    if (!trimmedName) {
      handleCancelRename();
      return;
    }

    try {
      const opts = await getFetchOptions();
      const response = await chatHistoryClient.renameChat(workflowName, chatId, trimmedName, opts);
      if (response.success) {
        setChats((prev) =>
          prev.map((c) =>
            c.id === chatId ? { ...c, name: response.name, updatedAt: response.updatedAt } : c
          )
        );
      } else {
        setError(response.message || 'Failed to rename chat');
      }
    } catch (err) {
      console.error('Failed to rename chat:', err);
      setError('Failed to rename chat');
    } finally {
      setRenamingChatId(null);
      setRenameValue('');
    }
  };

  const handleRenameKeyDown = (chatId: string, event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleConfirmRename(chatId, event);
    } else if (event.key === 'Escape') {
      handleCancelRename();
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className={styles.panel}>
      {/* Header with count and new chat button */}
      <div className={styles.listHeader}>
        <Text className={styles.chatCount}>
          <span className={styles.chatCountNumber}>{chats.length}</span>
          {' '}conversation{chats.length !== 1 ? 's' : ''}
        </Text>
        <button
          className={styles.newButton}
          onClick={onNewChat}
          type="button"
        >
          <AddRegular fontSize={14} />
          <span>New</span>
        </button>
      </div>

      <div className={styles.content}>
        {isLoading && (
          <div className={styles.loadingContainer}>
            <Spinner size="small" label="Loading..." />
          </div>
        )}

        {error && (
          <div className={styles.emptyState}>
            <Text className={styles.emptyTitle}>{error}</Text>
            <Button appearance="secondary" size="small" onClick={() => loadChatsRef.current?.(true)}>
              Retry
            </Button>
          </div>
        )}

        {!isLoading && !error && chats.length === 0 && (
          <div className={styles.emptyState}>
            <ChatMultipleRegular className={styles.emptyIcon} />
            <Text className={styles.emptyTitle}>No conversations yet</Text>
            <Text className={styles.emptySubtitle}>
              Start a new conversation with the assistant to see your history here.
            </Text>
            <Button
              appearance="primary"
              size="small"
              icon={<AddRegular />}
              onClick={onNewChat}
            >
              Start conversation
            </Button>
          </div>
        )}

        {!isLoading && !error && chats.length > 0 && (
          <div className={styles.chatList}>
            {chats.map((chat) => (
              <div key={chat.id} className={styles.chatItemWrapper}>
                {hoveredChatId === chat.id && tooltipPos && renamingChatId !== chat.id && (
                  <div
                    className={styles.customTooltip}
                    style={{ left: tooltipPos.x - 310, top: tooltipPos.y }}
                  >
                    {chat.name}
                  </div>
                )}
                <button
                  className={mergeClasses(
                    styles.chatCard,
                    activeChatId === chat.id && styles.chatCardSelected
                  )}
                  onClick={() => renamingChatId !== chat.id && handleOpenChat(chat.id)}
                  onMouseEnter={(e) => {
                    setHoveredChatId(chat.id);
                    setTooltipPos({ x: e.clientX, y: e.clientY });
                  }}
                  onMouseLeave={() => {
                    setHoveredChatId(null);
                    setTooltipPos(null);
                  }}
                  type="button"
                >
                  <div className={styles.chatIcon}>
                    <ChatMultipleRegular />
                  </div>
                  <div className={styles.chatInfo}>
                    {renamingChatId === chat.id ? (
                      <div className={styles.renameContainer}>
                        <Input
                          autoFocus
                          className={styles.renameInput}
                          size="small"
                          value={renameValue}
                          onChange={(_, data) => setRenameValue(data.value)}
                          onKeyDown={(e) => handleRenameKeyDown(chat.id, e)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className={styles.renameActions}>
                          <Button
                            appearance="subtle"
                            size="small"
                            icon={<CheckmarkRegular />}
                            onClick={(e) => handleConfirmRename(chat.id, e)}
                            title="Confirm rename"
                          />
                          <Button
                            appearance="subtle"
                            size="small"
                            icon={<DismissRegular />}
                            onClick={handleCancelRename}
                            title="Cancel rename"
                          />
                        </div>
                      </div>
                    ) : (
                      <Text className={styles.chatName}>
                        {chat.name}
                      </Text>
                    )}
                    <Text className={styles.chatMeta}>
                      {chat.messageCount} messages · {formatChatDate(chat.updatedAt)}
                    </Text>
                  </div>
                  <div
                    className={mergeClasses(
                      styles.chatActions,
                      hoveredChatId === chat.id && renamingChatId !== chat.id && styles.chatActionsVisible
                    )}
                  >
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<EditRegular />}
                      onClick={(e) => handleStartRename(chat.id, chat.name, e)}
                      title="Rename"
                    />
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<DeleteRegular />}
                      onClick={(e) => handleDeleteChat(chat.id, e)}
                      title="Delete"
                    />
                  </div>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={styles.footer}>
        {chats.length} / {maxChats} conversations saved
      </div>
    </div>
  );
};
